"use client";

import { clsx } from "@trydig/ui";
import { lazy, Suspense, useMemo } from "react";
import { ClientArticleFilter } from "~/components/bloks/ArticleListingPageBlok/ClientArticleFilter";
import {
	SortOptions,
	useArticleFilters,
} from "~/components/bloks/ArticleListingPageBlok/useArticleFilter";
import { ProductGrid } from "~/features/products/ProductGrid";
import type { BlokData } from "~/srpc/routes/story.router";
import styles from "./ArticleListingPageBlok.module.css";

/**
 * Dynamic import to avoid unknown Next + SB circular dependency reference error.
 * Running with SSR:true. So page is still rendered on server.
 */
const CardDynamic = lazy(() =>
	import("../ArticleCardBlok/ArticleCardBlok").then((mod) => ({
		default: mod.Card,
	})),
);

const UnsuspendedClientArticleGrid = ({
	articles,
	preview,
}: {
	articles?: BlokData["featuredArticle"]["articles"];
	preview: boolean;
}) => {
	const { sort } = useArticleFilters();

	const sortedArticles = useMemo(() => {
		const newArticles = [...articles!];
		if (sort === SortOptions.Newest) {
			newArticles?.sort((a, b) => {
				return (
					new Date(b.first_published_at || new Date()).getTime() -
					new Date(a.first_published_at || new Date()).getTime()
				);
			});
		} else {
			newArticles?.sort((a, b) => {
				return (
					new Date(a.first_published_at || new Date()).getTime() -
					new Date(b.first_published_at || new Date()).getTime()
				);
			});
		}

		return newArticles;
	}, [articles, sort]);

	return (
		<section className={clsx(styles.products, "alp__grid")} data-layout="page">
			<ClientArticleFilter count={articles?.length} />

			<ProductGrid>
				{sortedArticles?.map((article) => {
					return (
						<Suspense key={article.uuid}>
							<CardDynamic article={article} preview={preview} />
						</Suspense>
					);
				})}
			</ProductGrid>
		</section>
	);
};

export const ClientArticleGrid: typeof UnsuspendedClientArticleGrid = (props) => {
	return (
		<Suspense>
			<UnsuspendedClientArticleGrid {...props} />
		</Suspense>
	);
};
